import { useState } from 'react'
import { useParams } from 'react-router'
import LoadingSpinner from '../icons/LoadingSpinner.tsx'
import {
  useClubAdminCreateScheduleEntryMutation,
  useClubAdminDeleteScheduleEntryMutation,
  useClubAdminListAddressesQuery,
  useClubAdminListScheduleQuery,
  useClubAdminUpdateScheduleEntryMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import { useAppAuth } from '../../app/hooks/UseAppAuth.ts'
import { RecurringMeeting } from '../../generated'
import { getApiErrorCode, getApiErrorMessage } from '../../utils/ApiError.ts'
import {
  buildRrule,
  fromEpochSeconds,
  parseRrule,
  toEpochSeconds,
  type RecurrenceState,
} from '../../utils/Rrule.ts'
import ScheduleList from './ScheduleList.tsx'
import ScheduleFormDialog, {
  type ScheduleFormState,
} from '../modals/ScheduleFormDialog.tsx'
import SimpleModalDialog from '../modals/SimpleModalDialog.tsx'
import ConfirmActionDialog from '../modals/ConfirmActionDialog.tsx'

interface ScheduleManagementHeaderProps {
  total: number
  onAddMeeting: () => void
}

const ScheduleManagementHeader = ({
  total,
  onAddMeeting,
}: ScheduleManagementHeaderProps) => (
  <header className="mb-6 py-2 flex items-center justify-between gap-3">
    <h3 className="font-semibold text-gray-900">Meetings ({total})</h3>

    <button
      type="button"
      onClick={onAddMeeting}
      className="inline-flex items-center justify-center gap-x-2 rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
      Add meeting
    </button>
  </header>
)

const defaultRecurrence: RecurrenceState = {
  frequency: 'WEEKLY',
  weekdays: [],
  monthlyWeekday: 'MO',
  monthlyPosition: 1,
  monthlyDayOfMonth: 1,
}

const emptyForm: ScheduleFormState = {
  name: '',
  recurrence: defaultRecurrence,
  startDate: '',
  startTime: '',
  durationMin: 60,
  isRegularEvent: true,
  addressId: '',
}

const toFormState = (meeting: RecurringMeeting): ScheduleFormState => {
  const { date, time } = fromEpochSeconds(meeting.startTimestamp)
  return {
    name: meeting.name,
    recurrence: parseRrule(meeting.rrule),
    startDate: date,
    startTime: time,
    durationMin: meeting.durationMin,
    isRegularEvent: meeting.isRegularEvent,
    addressId: meeting.addressId,
  }
}

// Read the HTTP status off an RTK Query error when present.
const getHttpStatus = (error: unknown): number | undefined => {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: unknown }).status
    if (typeof status === 'number') return status
  }
  return undefined
}

const ScheduleManagement = () => {
  const { clubId = '' } = useParams()
  const { userLoaded } = useAppAuth()

  const skip = !clubId || !userLoaded

  const {
    data: meetings,
    isLoading,
    isError,
    isSuccess,
  } = useClubAdminListScheduleQuery(clubId, { skip })

  const { data: addresses, refetch: refetchAddresses } =
    useClubAdminListAddressesQuery(clubId, { skip })

  const [createScheduleEntry, { isLoading: isCreating }] =
    useClubAdminCreateScheduleEntryMutation()
  const [updateScheduleEntry, { isLoading: isUpdating }] =
    useClubAdminUpdateScheduleEntryMutation()
  const [deleteScheduleEntry] = useClubAdminDeleteScheduleEntryMutation()

  const [modalState, setModalState] = useState({
    isOpen: false,
    success: true,
    title: '',
    description: '',
    buttonText: 'Close',
  })

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editing, setEditing] = useState<RecurringMeeting | null>(null)
  const [formState, setFormState] = useState<ScheduleFormState>(emptyForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    meetingId: string
    name: string
  } | null>(null)

  const addressList = addresses ?? []

  const openModal = ({
    success,
    title,
    description,
    buttonText,
  }: {
    success: boolean
    title: string
    description: string
    buttonText?: string
  }) => {
    setModalState({
      isOpen: true,
      success,
      title,
      description,
      buttonText: buttonText ?? 'Close',
    })
  }

  const closeModal = () => {
    setModalState((previous) => ({ ...previous, isOpen: false }))
  }

  const handleAddMeeting = () => {
    setEditing(null)
    setFormState(emptyForm)
    setFormError(null)
    setIsDialogOpen(true)
  }

  const handleEditMeeting = (meeting: RecurringMeeting) => {
    setEditing(meeting)
    setFormState(toFormState(meeting))
    setFormError(null)
    setIsDialogOpen(true)
  }

  const closeDialog = () => {
    setIsDialogOpen(false)
    setFormError(null)
  }

  const handleFieldChange = <K extends keyof ScheduleFormState>(
    field: K,
    value: ScheduleFormState[K]
  ) => {
    setFormState((previous) => ({ ...previous, [field]: value }))
  }

  // Turn an API error into a clear, field-aware message for the dialog.
  const resolveScheduleErrorMessage = (error: unknown): string => {
    const status = getHttpStatus(error)
    if (status === 401 || status === 403) {
      return "You don't have permission to manage this club's schedule."
    }

    switch (getApiErrorCode(error)) {
      case 'INVALID_RRULE':
        return "That recurrence isn't valid. Adjust the pattern and try again."
      case 'DUPLICATE_RECURRING_RRULE':
        return 'A meeting with this exact recurrence already exists. Change the day/frequency.'
      case 'ADDRESS_NOT_FOUND':
        // The address vanished out from under us; refresh the picker options.
        refetchAddresses()
        return 'That address no longer exists. Pick another.'
      case 'VALIDATION_ERROR':
        return 'Please check the fields and try again.'
      default:
        return getApiErrorMessage(error, 'Unable to save meeting. Try again later')
    }
  }

  const submitMeeting = async () => {
    const name = formState.name.trim()

    if (!name) {
      setFormError('Meeting name is required')
      return
    }

    if (
      formState.recurrence.frequency === 'WEEKLY' &&
      formState.recurrence.weekdays.length === 0
    ) {
      setFormError('Pick at least one weekday for the recurrence')
      return
    }

    if (!formState.startDate || !formState.startTime) {
      setFormError('Set the first occurrence date and start time')
      return
    }

    const startTimestamp = toEpochSeconds(
      formState.startDate,
      formState.startTime
    )
    if (!Number.isFinite(startTimestamp) || startTimestamp <= 0) {
      setFormError('Set a valid first occurrence date and start time')
      return
    }

    if (
      !Number.isInteger(formState.durationMin) ||
      formState.durationMin <= 0
    ) {
      setFormError('Duration must be a positive number of minutes')
      return
    }

    if (formState.addressId === '') {
      setFormError(
        addressList.length === 0
          ? 'Add an address first, then attach it to this meeting'
          : 'Select an address for this meeting'
      )
      return
    }

    if (!clubId) {
      setFormError('Club context is missing. Please reload the page and try again')
      return
    }

    let rrule: string
    try {
      rrule = buildRrule(formState.recurrence)
    } catch (error) {
      console.error('Failed to build rrule from recurrence state', error)
      setFormError("That recurrence isn't valid. Adjust the pattern and try again.")
      return
    }

    const body = {
      name,
      rrule,
      startTimestamp,
      durationMin: formState.durationMin,
      isRegularEvent: formState.isRegularEvent,
      addressId: Number(formState.addressId),
    }

    setFormError(null)

    try {
      if (editing) {
        await updateScheduleEntry({
          clubId,
          meetingId: String(editing.id),
          body,
        }).unwrap()
      } else {
        await createScheduleEntry({ clubId, body }).unwrap()
      }

      setIsDialogOpen(false)
      openModal({
        success: true,
        title: 'Meeting saved',
        description: `${name} has been saved to your club schedule.`,
      })
    } catch (error) {
      console.error('Failed to save recurring meeting', error)
      setFormError(resolveScheduleErrorMessage(error))
    }
  }

  const handleRemoveMeeting = (payload: {
    meetingId: string
    name: string
  }) => {
    setDeleteConfirmation(payload)
  }

  const confirmRemoveMeeting = async () => {
    if (!deleteConfirmation || !clubId) {
      setDeleteConfirmation(null)
      return
    }

    const target = deleteConfirmation
    setDeleteConfirmation(null)

    try {
      await deleteScheduleEntry({
        clubId,
        meetingId: target.meetingId,
      }).unwrap()

      openModal({
        success: true,
        title: 'Meeting removed',
        description: `${target.name} was removed. This stops future occurrences.`,
      })
    } catch (error) {
      console.error('Failed to remove recurring meeting', error)

      const status = getHttpStatus(error)
      if (status === 401 || status === 403) {
        openModal({
          success: false,
          title: 'Removal failed',
          description:
            "You don't have permission to manage this club's schedule.",
        })
        return
      }

      openModal({
        success: false,
        title: 'Removal failed',
        description: getApiErrorMessage(
          error,
          `Unable to remove ${target.name}. Try again later`
        ),
      })
    }
  }

  if (isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <LoadingSpinner message="Loading meetings..." />
      </div>
    )
  }

  if (isError) {
    return (
      <p className="text-red-600">Unable to load club meetings right now</p>
    )
  }

  return (
    <div className="py-b border-b">
      <ScheduleManagementHeader
        total={meetings?.length ?? 0}
        onAddMeeting={handleAddMeeting}
      />

      {addressList.length === 0 && (
        <p className="mb-3 text-sm text-amber-600">
          Add an address first — every meeting needs a location.
        </p>
      )}

      {isSuccess && meetings && meetings.length > 0 && (
        <ScheduleList
          meetings={meetings}
          addresses={addressList}
          onEdit={handleEditMeeting}
          onRemove={handleRemoveMeeting}
        />
      )}

      {isSuccess && (!meetings || meetings.length === 0) && (
        <p className="text-sm text-gray-600">
          No recurring meetings yet. Add one to publish your schedule.
        </p>
      )}

      <ScheduleFormDialog
        isOpen={isDialogOpen}
        mode={editing ? 'edit' : 'create'}
        onClose={closeDialog}
        formState={formState}
        addresses={addressList}
        onFieldChange={handleFieldChange}
        onSubmit={submitMeeting}
        isSubmitting={isCreating || isUpdating}
        error={formError}
      />

      <SimpleModalDialog
        isOpen={modalState.isOpen}
        onClose={closeModal}
        success={modalState.success}
        title={modalState.title}
        description={modalState.description}
        buttonText={modalState.buttonText}
      />

      <ConfirmActionDialog
        isOpen={Boolean(deleteConfirmation)}
        title="Remove meeting?"
        description={
          deleteConfirmation
            ? `Remove ${deleteConfirmation.name}? This stops future occurrences.`
            : ''
        }
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={confirmRemoveMeeting}
        onCancel={() => setDeleteConfirmation(null)}
      />
    </div>
  )
}

export default ScheduleManagement
